import { Controller } from "@hotwired/stimulus"

// Address of a customer or a branch. The form shows one read-only summary line
// with an edit button; the real street/city/zipcode/country inputs live in a
// modal, so they still submit with the surrounding form.
export default class extends Controller {
  static targets = ["summary", "modal", "street", "city", "zipcode", "country"]

  connect() {
    this.defaultCountry = this.countryTarget.options[0]?.value
    this.render()
  }

  open() {
    this.reverted = this.read()
    this.modalTarget.classList.remove("hidden")
    this.streetTarget.focus()
  }

  cancel() {
    this.write(this.reverted)
    this.render()
    this.close()
  }

  submit() {
    this.render()
    this.close()
    // The real inputs sit in the modal, so let the summary announce the change
    // and the form clears the address error the way it does for plain fields.
    this.summaryTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  backdropClose(event) {
    if (event.target === this.modalTarget) this.cancel()
  }

  keydown(event) {
    if (event.key === "Escape") this.cancel()
    if (event.key === "Enter") {
      event.preventDefault()
      this.submit()
    }
  }

  assign(address) {
    this.write({
      street: address?.street || "",
      city: address?.city || "",
      zipcode: address?.zipcode || "",
      country_alpha2: address?.country_alpha2 || "",
    })
    this.render()
  }

  read() {
    return {
      street: this.streetTarget.value,
      city: this.cityTarget.value,
      zipcode: this.zipcodeTarget.value,
      country_alpha2: this.countryTarget.value,
    }
  }

  write(address) {
    this.streetTarget.value = address.street
    this.cityTarget.value = address.city
    this.zipcodeTarget.value = address.zipcode
    this.countryTarget.value = address.country_alpha2 || this.defaultCountry
  }

  // Mirrors Address#display_name so the summary reads the same before and after a
  // save. The country defaults to Slovakia, so on its own it is not an address yet.
  render() {
    const { street, city, zipcode } = this.read()

    this.summaryTarget.value = [street, city, zipcode].some(Boolean)
      ? [street, [zipcode, city].filter(Boolean).join(" "), this.countryName].filter(Boolean).join(", ")
      : ""
  }

  get countryName() {
    return this.countryTarget.selectedOptions[0]?.text || ""
  }
}
